
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import ShoppingCart from '../../components/feature/ShoppingCart';
import { Link } from 'react-router-dom';

interface Book {
  id: string;
  title: string;
  description: string;
  category: string;
  price: number;
  digital_price: number;
  image_url: string;
  rating: number;
  reviews_count: number;
  stock_quantity: number;
  type: 'book' | 'apparel' | 'digital';
}

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [sortBy, setSortBy] = useState('featured');
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  // Initialize filters from URL parameters
  useEffect(() => {
    const typeParam = searchParams.get('type');
    const categoryParam = searchParams.get('category');
    const sortParam = searchParams.get('sort');

    if (typeParam && ['book', 'apparel', 'digital'].includes(typeParam)) {
      setSelectedType(typeParam);
    } else {
      setSelectedType('All');
    }

    if (categoryParam) {
      setSelectedCategory(categoryParam);
    } else {
      setSelectedCategory('All');
    }

    if (sortParam) {
      setSortBy(sortParam);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchBooks();
    updateCartCount();

    // Check for payment success/cancel from URL params
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');

    if (success) {
      // Clear cart on successful payment
      localStorage.removeItem('bookCart');
      updateCartCount();
      alert('Payment successful! Your order has been confirmed. You will receive an email confirmation shortly.');
      // Clear URL params
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('success');
      setSearchParams(newSearchParams, { replace: true });
    } else if (canceled) {
      alert('Payment was cancelled. Your items are still in your cart.');
      // Clear URL params
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('canceled');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [selectedCategory, selectedType, sortBy, searchParams, setSearchParams]);

  const updateURLParams = (type: string, category: string = 'All', sort: string = sortBy) => {
    const newSearchParams = new URLSearchParams();
    
    if (type !== 'All') {
      newSearchParams.set('type', type);
    }
    
    if (category !== 'All') {
      newSearchParams.set('category', category);
    }
    
    if (sort !== 'featured') {
      newSearchParams.set('sort', sort);
    }

    setSearchParams(newSearchParams, { replace: true });
  };

  const fetchBooks = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory !== 'All') {
        params.append('category', selectedCategory);
      }
      if (selectedType !== 'All') {
        params.append('type', selectedType);
      }
      params.append('sortBy', sortBy);

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-books?${params}`, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        }
      });

      const result = await response.json();
      if (result.books) {
        setBooks(result.books);
      }
    } catch (error) {
      console.error('Error fetching books:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateCartCount = () => {
    const savedCart = localStorage.getItem('bookCart');
    if (savedCart) {
      const cartItems = JSON.parse(savedCart);
      const count = cartItems.reduce((total: number, item: any) => total + item.quantity, 0);
      setCartCount(count);
    }
  };

  const addToCart = (book: Book, format: 'physical' | 'digital') => {
    const savedCart = localStorage.getItem('bookCart');
    const cartItems = savedCart ? JSON.parse(savedCart) : [];
    
    const existingItemIndex = cartItems.findIndex((item: any) => 
      item.id === book.id && item.format === format
    );

    if (existingItemIndex >= 0) {
      cartItems[existingItemIndex].quantity += 1;
    } else {
      cartItems.push({
        id: book.id,
        title: book.title,
        price: book.price,
        digitalPrice: book.digital_price,
        format: format,
        quantity: 1,
        image: book.image_url
      });
    }

    localStorage.setItem('bookCart', JSON.stringify(cartItems));
    updateCartCount();
    
    // Show success message
    alert(`${book.title} (${format}) added to cart!`);
  };

  const handleTypeChange = (newType: string) => {
    setSelectedType(newType);
    setSelectedCategory('All');
    updateURLParams(newType, 'All', sortBy);
  };

  const handleCategoryChange = (newCategory: string) => {
    setSelectedCategory(newCategory);
    updateURLParams(selectedType, newCategory, sortBy);
  };

  const handleSortChange = (newSort: string) => {
    setSortBy(newSort);
    updateURLParams(selectedType, selectedCategory, newSort);
  };

  const productTypes = [
    { key: 'All', label: 'All Products', icon: 'ri-store-line' },
    { key: 'book', label: 'Books', icon: 'ri-book-line' },
    { key: 'apparel', label: 'Apparel', icon: 'ri-shirt-line' },
    { key: 'digital', label: 'Digital Products', icon: 'ri-download-line' }
  ];

  const bookCategories = ['Personal Development', 'Faith & Spirituality', 'Leadership', 'Journals & Workbooks'];

  const getFilteredCategories = () => {
    if (selectedType === 'book') {
      return ['Personal Development', 'Faith & Spirituality', 'Leadership'];
    } else if (selectedType === 'apparel') {
      return ['T-Shirts', 'Hoodies', 'Accessories'];
    } else if (selectedType === 'digital') {
      return ['E-Books', 'Audio Books', 'Workbooks'];
    }
    return bookCategories;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-blue-800 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Loading products...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Cart Button */}
      <div className="fixed top-20 right-6 z-40">
        <button
          onClick={() => setCartOpen(true)}
          className="bg-blue-800 text-white p-3 rounded-full shadow-lg hover:bg-blue-900 transition-colors cursor-pointer"
        >
          <i className="ri-shopping-cart-line text-xl"></i>
          {cartCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
              {cartCount}
            </span>
          )}
        </button>
      </div>
      
      {/* Hero Section */}
      <section 
        className="relative py-24 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url('https://public.readdy.ai/ai/img_res/025b1b6e81c73e14dc93bf240f7415de.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">Our Store</h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            Discover transformative books, inspiring apparel, and powerful digital resources 
            designed to fuel your growth journey and deepen your faith.
          </p>
        </div>
      </section>

      {/* Product Type Navigation */}
      <section className="py-8 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {productTypes.map(type => (
              <button
                key={type.key}
                onClick={() => handleTypeChange(type.key)}
                className={`p-6 rounded-xl text-center transition-all cursor-pointer whitespace-nowrap ${
                  selectedType === type.key 
                    ? 'bg-blue-800 text-white shadow-lg transform scale-105' 
                    : 'bg-white text-slate-700 hover:bg-slate-100 shadow-md hover:shadow-lg'
                }`}
              >
                <div className={`w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full ${
                  selectedType === type.key ? 'bg-white/20' : 'bg-slate-100'
                }`}>
                  <i className={`${type.icon} text-2xl ${selectedType === type.key ? 'text-white' : 'text-blue-800'}`}></i>
                </div>
                <h3 className="font-semibold text-lg mb-1">{type.label}</h3>
                <p className={`text-sm ${selectedType === type.key ? 'text-white/80' : 'text-slate-500'}`}>
                  {type.key === 'All' ? 'Browse everything' : 
                   type.key === 'book' ? 'Physical & Digital' :
                   type.key === 'apparel' ? 'Clothing & Accessories' :
                   'Instant Downloads'}
                </p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleCategoryChange('All')}
                className={`px-4 py-2 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap ${
                  selectedCategory === 'All' 
                    ? 'bg-slate-800 text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                All {selectedType !== 'All' ? productTypes.find(t => t.key === selectedType)?.label : 'Categories'}
              </button>
              {getFilteredCategories().map(category => (
                <button
                  key={category}
                  onClick={() => handleCategoryChange(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap ${
                    selectedCategory === category 
                      ? 'bg-slate-800 text-white' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Sort */}
            <div className="flex items-center gap-4">
              <span className="text-slate-600">Sort by:</span>
              <select 
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 pr-8"
              >
                <option value="featured">Featured</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
                <option value="newest">Newest First</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {books.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center bg-slate-100 rounded-full mx-auto mb-4">
                <i className="ri-search-line text-2xl text-slate-400"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">No products found</h3>
              <p className="text-slate-600 mb-6">Try adjusting your filters or browse all categories</p>
              <Button onClick={() => {handleTypeChange('All'); handleCategoryChange('All');}}>
                Browse All Products
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-product-shop>
              {books.map(book => (
                <div key={book.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105">
                  <div className="relative">
                    <img 
                      src={book.image_url}
                      alt={book.title}
                      className="w-full h-72 object-contain bg-slate-50"
                    />
                    <div className="absolute top-3 left-3 flex flex-col gap-2">
                      <span className="px-2 py-1 bg-white/90 text-slate-600 text-xs rounded-full">
                        {book.category}
                      </span>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        book.type === 'book' ? 'bg-blue-100 text-blue-700' :
                        book.type === 'apparel' ? 'bg-purple-100 text-purple-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {book.type === 'book' ? 'Book' : book.type === 'apparel' ? 'Apparel' : 'Digital'}
                      </span>
                    </div>
                    {book.stock_quantity <= 5 && book.stock_quantity > 0 && (
                      <span className="absolute top-3 right-3 px-2 py-1 bg-red-100 text-red-600 text-xs rounded-full">
                        Only {book.stock_quantity} left
                      </span>
                    )}
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-slate-800 mb-2">{book.title}</h3>
                    <p className="text-slate-600 mb-4 text-sm">{book.description}</p>
                    
                    <div className="flex items-center mb-4">
                      <div className="flex text-yellow-400 mr-2">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className={`ri-star-${i < Math.floor(book.rating) ? 'fill' : 'line'} text-sm`}></i>
                        ))}
                      </div>
                      <span className="text-slate-600 text-sm">
                        {book.rating} ({book.reviews_count})
                      </span>
                    </div>
                    
                    <div className="mb-4">
                      {book.type === 'apparel' || book.digital_price === 0 ? (
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-bold text-slate-800">Price</span>
                          <span className="text-lg font-bold text-slate-800">${book.price}</span>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-lg font-bold text-slate-800">Physical</span>
                            <span className="text-lg font-bold text-slate-800">${book.price}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-slate-600">Digital</span>
                            <span className="text-slate-600">${book.digital_price}</span>
                          </div>
                        </>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      {book.digital_price === 0 ? (
                        <Button 
                          onClick={() => addToCart(book, 'physical')}
                          disabled={book.stock_quantity === 0}
                          className="w-full"
                        >
                          {book.stock_quantity === 0 ? 'Out of Stock' : 'Add to Cart'}
                        </Button>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          <Button 
                            onClick={() => addToCart(book, 'physical')}
                            disabled={book.stock_quantity === 0}
                            className="text-xs px-3 py-2 w-full"
                          >
                            {book.stock_quantity === 0 ? 'Out of Stock' : 'Physical'}
                          </Button>
                          <Button 
                            onClick={() => addToCart(book, 'digital')}
                            variant="outline" 
                            className="text-xs px-3 py-2 w-full"
                          >
                            Digital
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Categories */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-800 mb-4">Shop by Category</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Explore our carefully curated collections designed to inspire and transform your journey
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div 
              className="relative rounded-xl overflow-hidden cursor-pointer group"
              onClick={() => {handleTypeChange('book'); setSelectedCategory('All');}}
            >
              <img 
                src="https://readdy.ai/api/search-image?query=Stack%20of%20inspirational%20books%20with%20warm%20lighting%2C%20personal%20development%20and%20faith%20themed%20books%2C%20cozy%20reading%20atmosphere%2C%20modern%20book%20covers%2C%20motivational%20titles%2C%20professional%20book%20photography&width=400&height=300&seq=books001&orientation=landscape"
                alt="Books Collection"
                className="w-full h-64 object-cover transition-transform group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
                <div className="p-6 text-white">
                  <h3 className="text-xl font-bold mb-2">Books</h3>
                  <p className="text-white/90">Physical &amp; Digital</p>
                </div>
              </div>
            </div>
            
            <div 
              className="relative rounded-xl overflow-hidden cursor-pointer group"
              onClick={() => {handleTypeChange('apparel'); setSelectedCategory('All');}}
            >
              <img 
                src="https://readdy.ai/api/search-image?query=Premium%20inspirational%20t-shirts%20and%20hoodies%20displayed%20on%20hangers%2C%20motivational%20quotes%20and%20designs%2C%20comfortable%20casual%20wear%2C%20modern%20apparel%20photography%2C%20lifestyle%20clothing%2C%20faith-based%20fashion&width=400&height=300&seq=apparel001&orientation=landscape"
                alt="Apparel Collection"
                className="w-full h-64 object-cover transition-transform group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
                <div className="p-6 text-white">
                  <h3 className="text-xl font-bold mb-2">Apparel</h3>
                  <p className="text-white/90">Clothing &amp; Accessories</p>
                </div>
              </div>
            </div>
            
            <div 
              className="relative rounded-xl overflow-hidden cursor-pointer group"
              onClick={() => {handleTypeChange('digital'); setSelectedCategory('All');}}
            >
              <img 
                src="https://readdy.ai/api/search-image?query=Digital%20tablet%20displaying%20e-books%20and%20digital%20downloads%2C%20modern%20technology%20setup%2C%20online%20learning%20resources%2C%20digital%20library%20interface%2C%20instant%20access%20content%2C%20professional%20tech%20photography&width=400&height=300&seq=digital001&orientation=landscape"
                alt="Digital Products"
                className="w-full h-64 object-cover transition-transform group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
                <div className="p-6 text-white">
                  <h3 className="text-xl font-bold mb-2">Digital Products</h3>
                  <p className="text-white/90">Instant Downloads</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Promo Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-800 mb-6">Special Offers</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 shadow-lg">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-800 text-white rounded-full mx-auto mb-4">
                <i className="ri-user-star-line text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-4">First-Time Reader Discount</h3>
              <p className="text-slate-600 mb-4">
                New customers get 15% off their first order. Use code <span className="font-bold text-blue-600">WELCOME15</span> at checkout.
              </p>
              <Button 
                onClick={() => setCartOpen(true)}
                variant="outline"
                className="cursor-pointer"
              >
                Shop Now
              </Button>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to purple-100 rounded-lg p-6 shadow-lg">
              <div className="w-12 h-12 flex items-center justify-center bg-purple-800 text-white rounded-full mx-auto mb-4">
                <i className="ri-download-line text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-4">Digital Bundle Deals</h3>
              <p className="text-slate-600 mb-4">
                Save when you buy digital versions. Perfect for instant access to your favorite content.
              </p>
              <Button 
                onClick={() => {handleTypeChange('digital'); setSelectedCategory('All');}}
                variant="outline"
                className="cursor-pointer"
              >
                Browse Digital
              </Button>
            </div>
          </div>
        </div>
      </section>

      <ShoppingCart isOpen={cartOpen} onClose={() => setCartOpen(false)} />
      <Footer />
    </div>
  );
}
